import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { catalogosService } from '../../services/catalogosService';
import { Clase, Ramo, Linea } from '../../types';
import { useSnackbar } from 'notistack';
import { SelectChangeEvent } from '@mui/material/Select';
// import productoService from '../../services/productoService';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const Clases: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [clases, setClases] = useState<Clase[]>([]);
  const [ramos, setRamos] = useState<Ramo[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRamo, setSelectedRamo] = useState('');
  const [selectedLinea, setSelectedLinea] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Columnas de la plantilla para Clases (según API /api/Catalogos/clases)
  const columnasClases = [
    'codigoClase',
    'nombreClase',
    'codigoRamo',
    'codigoLinea',
    'estadoClase'
  ];
  const camposObligatoriosClases = ['codigoClase','nombreClase','estadoClase'];

  const loadRamos = useCallback(async () => {
    try {
      const response = await catalogosService.getRamos(1, 1000);
      if (response && response.items) {
        setRamos(response.items);
      }
    } catch (error) {
      console.error('Error al cargar ramos:', error);
      enqueueSnackbar('Error al cargar los ramos', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadLineas = useCallback(async (codigoRamo: string) => {
    try {
      const response = await catalogosService.getLineas(1, 1000, '', codigoRamo);
      if (response && response.items) {
        setLineas(response.items);
      }
    } catch (error) {
      console.error('Error al cargar líneas:', error);
      enqueueSnackbar('Error al cargar las líneas', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadClases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await catalogosService.getClases(
        page + 1,
        rowsPerPage,
        searchTerm,
        selectedRamo,
        selectedLinea
      );
      
      if (response && response.items) {
        setClases(response.items);
        setTotalCount(response.totalCount);
      } else {
        setClases([]);
        setTotalCount(0);
        setError('No se pudo cargar la información de clases');
      }
    } catch (error) {
      console.error('Error al cargar clases:', error);
      setClases([]);
      setTotalCount(0);
      setError('Error al cargar las clases');
      enqueueSnackbar('Error al cargar las clases', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, selectedRamo, selectedLinea, enqueueSnackbar]);

  useEffect(() => {
    loadRamos();
  }, [loadRamos]);

  useEffect(() => {
    if (selectedRamo) {
      loadLineas(selectedRamo);
      setSelectedLinea('');
    } else {
      setLineas([]);
      setSelectedLinea('');
    }
  }, [selectedRamo, loadLineas]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadClases();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm, selectedRamo, selectedLinea, loadClases]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0);
  };

  const handleRamoChange = (event: SelectChangeEvent<string>) => {
    setSelectedRamo(event.target.value);
    setPage(0);
  };

  const handleLineaChange = (event: SelectChangeEvent<string>) => {
    setSelectedLinea(event.target.value);
    setPage(0);
  };

  const handleDelete = async (codigoClase: string) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta clase?')) {
      try {
        await catalogosService.deleteClase(codigoClase);
        enqueueSnackbar('Clase eliminada con éxito', { variant: 'success' });
        loadClases();
      } catch (error) {
        console.error('Error al eliminar clase:', error);
        enqueueSnackbar('Error al eliminar la clase', { variant: 'error' });
      }
    }
  };

  const handleDescargarPlantilla = () => {
    try {
      const data = [columnasClases, new Array(columnasClases.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Clases');
      // Hoja de instrucciones con valores aceptados
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['codigoClase', 'Código único de la clase', 'Texto alfanumérico (obligatorio)'],
        ['nombreClase', 'Nombre de la clase', 'Texto (obligatorio)'],
        ['codigoRamo', 'Ramo al que pertenece', 'Código de ramo existente (opcional)'],
        ['codigoLinea', 'Línea a la que pertenece', 'Código de línea existente (opcional)'],
        ['estadoClase', 'Estado de la clase', 'Activo o Inactivo (obligatorio)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      XLSX.writeFile(wb, 'PlantillaClases.xlsx');
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', { variant: 'success', autoHideDuration: 4000 });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', { variant: 'error', autoHideDuration: 4000 });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      enqueueSnackbar('Archivo leído, procesando filas.', { variant: 'info', autoHideDuration: 3000 });
      if (!rows.length) { enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 }); return; }
      const headersRaw = Object.keys(rows[0] as any);
      const headersNorm = new Map(headersRaw.map(h => [h.trim().toLowerCase(), h]));
      const noEncontrados = columnasClases.filter(h => !headersNorm.has(h.toLowerCase()));
      if (noEncontrados.length > 0) { enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 5000 }); return; }
      const getVal = (r: any, campo: string) => { const k = headersNorm.get(campo.toLowerCase()) ?? campo; return r[k]; };

      // Precargas para validación y duplicados
      const existentes = await catalogosService.getClases(1, 10000, '', '', '');
      const existeCodigo = new Set((existentes.items || []).map((c: any) => String(c.codigoClase).trim()));
      const ramosAll = await catalogosService.getRamos(1, 10000, '');
      const existeRamo = new Set((ramosAll.items || []).map((r: any) => String(r.codigoRamo).trim()));
      const lineasAll = await catalogosService.getLineas(1, 10000, '', '');
      const lineaToRamo = new Map<string, string>((lineasAll.items || []).map((l: any) => [String(l.codigoLinea).trim(), String(l.codigoRamo).trim()]));

      let exitos = 0, fallos = 0;
      for (let i = 0; i < rows.length; i++) {
        const row: any = rows[i];
        const rowNorm: Record<string, any> = {};
        columnasClases.forEach(c => { rowNorm[c] = getVal(row, c); });
        // Validar obligatorios
        const faltan: string[] = [];
        for (const campo of camposObligatoriosClases) {
          if (rowNorm[campo] === undefined || rowNorm[campo] === null || String(rowNorm[campo]).trim() === '') {
            faltan.push(campo);
          }
        }
        if (faltan.length > 0) { enqueueSnackbar(`❌ Fila ${i + 2}: Falta completar: ${faltan.join(', ')}`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }

        // Validación de tipos: campos convertibles a string (Excel exporta números, booleanos, etc. como esos tipos)
        const camposString = ['codigoClase','nombreClase','estadoClase','codigoRamo','codigoLinea'];
        const tiposInvalidos: string[] = [];
        for (const campo of camposString) {
          const valor = rowNorm[campo];
          if (valor === undefined || valor === null || String(valor).trim() === '') continue; // vacíos ya validados arriba si obligatorios
          // Aceptar string, number, boolean (Excel puede exportar "1", "001" como number)
          const tipo = typeof valor;
          if (tipo !== 'string' && tipo !== 'number' && tipo !== 'boolean') tiposInvalidos.push(`${campo} (esperado: string)`);
        }
        if (tiposInvalidos.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: Tipo de dato inválido en ${tiposInvalidos.join(', ')}`, { variant: 'error', autoHideDuration: 9000 });
          fallos++; continue;
        }

        const codigoClase = String(rowNorm.codigoClase).trim();
        if (existeCodigo.has(codigoClase)) { enqueueSnackbar(`🔴 Clase duplicada (codigoClase: ${codigoClase})`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }

        // Normalizar estadoClase a ACTIVO/INACTIVO y validar valores permitidos (case-insensitive)
        const estadoRaw = String(rowNorm.estadoClase).trim();
        const estadoLower = estadoRaw.toLowerCase();
        if (estadoLower !== 'activo' && estadoLower !== 'inactivo') {
          enqueueSnackbar(`❌ Fila ${i + 2}: estadoClase inválido (use "Activo" o "Inactivo")`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }
        const estadoNormalizado = estadoLower === 'activo' ? 'ACTIVO' : 'INACTIVO';

        const codigoRamo = String(rowNorm.codigoRamo ?? '').trim();
        if (codigoRamo !== '' && !existeRamo.has(codigoRamo)) { enqueueSnackbar(`❌ Fila ${i + 2}: codigoRamo inexistente (${codigoRamo})`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }

        const codigoLinea = String(rowNorm.codigoLinea ?? '').trim();
        const lineaRamo = codigoLinea !== '' ? lineaToRamo.get(codigoLinea) : undefined;
        if (codigoLinea !== '' && !lineaRamo) { enqueueSnackbar(`❌ Fila ${i + 2}: codigoLinea inexistente (${codigoLinea})`, { variant: 'error', autoHideDuration: 8000 }); fallos++; continue; }
        if (codigoLinea !== '' && codigoRamo !== '' && lineaRamo !== codigoRamo) { enqueueSnackbar(`❌ Fila ${i + 2}: La línea ${codigoLinea} no pertenece al ramo ${codigoRamo}`, { variant: 'error', autoHideDuration: 9000 }); fallos++; continue; }

        const payload: any = {
          codigoClase,
          nombreClase: String(rowNorm.nombreClase).trim(),
          codigoRamo: codigoRamo !== '' ? codigoRamo : null,
          codigoLinea: codigoLinea !== '' ? codigoLinea : null,
          estadoClase: estadoNormalizado,
        };

        try {
          await catalogosService.createClase(payload);
          exitos++;
          existeCodigo.add(codigoClase);
        } catch (err: any) {
          let mensajePlano = err?.response?.data?.message || err?.response?.data || err?.message || 'Error en backend';
          if (err?.response?.data?.errors) {
            const errores = err.response.data.errors; let detalle = '';
            for (let campo in errores) { if (Array.isArray(errores[campo])) { detalle += `${campo}: ${errores[campo].join('; ')}\n`; } }
            enqueueSnackbar('❌ Error en los datos: ' + detalle, { variant: 'error', autoHideDuration: 12000 }); fallos++; continue;
          }
          if (typeof mensajePlano !== 'string') { try { mensajePlano = JSON.stringify(mensajePlano); } catch {} }
          enqueueSnackbar(`❌ Fila ${i+2}: Error al guardar: ${String(mensajePlano).slice(0,120)}`, { variant: 'error', autoHideDuration: 9000 }); fallos++;
        }
      }

      enqueueSnackbar(`Importación de clases completada. Éxitos: ${exitos}, Fallos: ${fallos}`, { variant: fallos > 0 ? 'error' : 'success', autoHideDuration: 4500 });
      loadClases();
      setTimeout(() => { setOpenImportar(false); }, 800);
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Clases
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/clases/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(21, 128, 61, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(21, 128, 61, 0.4)',
                  }
                }}
              >
                Nueva Clase
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<AttachFileIcon />} 
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Importar clases
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }} 
              fullWidth={isMobile}
            >
              <InputLabel id="ramo-select-label">Filtrar por Ramo</InputLabel>
              <Select
                labelId="ramo-select-label"
                value={selectedRamo}
                onChange={handleRamoChange}
                label="Filtrar por Ramo"
              >
                <MenuItem value="">Todos</MenuItem>
                {ramos.map((ramo) => (
                  <MenuItem key={ramo.codigoRamo} value={ramo.codigoRamo}>
                    {ramo.nombreRamo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl 
              size="small" 
              sx={{ 
                minWidth: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }} 
              fullWidth={isMobile}
            >
              <InputLabel id="linea-select-label">Filtrar por Línea</InputLabel>
              <Select
                labelId="linea-select-label"
                value={selectedLinea}
                onChange={handleLineaChange}
                label="Filtrar por Línea"
                disabled={!selectedRamo}
              >
                <MenuItem value="">Todas</MenuItem>
                {lineas.map((linea) => (
                  <MenuItem key={linea.codigoLinea} value={linea.codigoLinea}>
                    {linea.nombreLinea}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography sx={{ color: 'error.main', textAlign: 'center', py: 2 }}>{error}</Typography>
              ) : clases.length === 0 ? (
                <Typography sx={{ textAlign: 'center', py: 2 }}>No se encontraron clases</Typography>
              ) : (
                clases.map((clase) => (
                  <Card 
                    key={clase.codigoClase}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {clase.nombreClase}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Código:</strong> {clase.codigoClase}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Ramo:</strong> {clase.codigoRamoNavigation?.nombreRamo || ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Línea:</strong> {clase.codigoLineaNavigation?.nombreLinea || ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Estado:</strong> {clase.estadoClase === 'ACTIVO' ? 'Activo' : 'Inactivo'}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => navigate(`/inventario/clases/${clase.codigoClase.trim()}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1565c0' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              onClick={() => handleDelete(clase.codigoClase.trim())}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#d32f2f' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ramo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Línea</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: 'error.main' }}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : clases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No se encontraron clases
                      </TableCell>
                    </TableRow>
                  ) : (
                    clases.map((clase) => (
                      <TableRow 
                        key={clase.codigoClase}
                        sx={{
                          '&:hover': {
                            bgcolor: '#f9f9f9',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{clase.codigoClase}</TableCell>
                        <TableCell>{clase.nombreClase}</TableCell>
                        <TableCell>{clase.codigoRamoNavigation?.nombreRamo || ''}</TableCell>
                        <TableCell>{clase.codigoLineaNavigation?.nombreLinea || ''}</TableCell>
                        <TableCell>{clase.estadoClase === 'ACTIVO' ? 'Activo' : 'Inactivo'}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton
                                onClick={() => navigate(`/inventario/clases/${clase.codigoClase.trim()}`)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#1565c0',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleDelete(clase.codigoClase.trim())}
                                size="small"
                                sx={{
                                  bgcolor: '#f44336',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: 32,
                                  height: 32,
                                  '&:hover': { 
                                    bgcolor: '#d32f2f',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de importación de clases */}
      <Dialog 
        open={openImportar} 
        onClose={() => setOpenImportar(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Clases</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar clases.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar las clases.
              </Typography>
              <label htmlFor="excel-upload-clases">
                <input id="excel-upload-clases" type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onImportChange} />
                <Button variant="contained" fullWidth color="primary" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clases; 